import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { firstError } from '../../utils/format.js'
import {
  getMfaStatus,
  enableMfa,
  disableMfa,
  regenerateRecoveryCodes,
  totpSetup,
  totpConfirm,
  totpDisable,
} from '../../api/auth.js'

/**
 * Recovery-code reveal — shown ONCE after enable / confirm / regenerate.
 */
function RecoveryCodesPanel({ codes, onDone }) {
  const [copied, setCopied] = useState(false)
  const text = codes.join('\n')

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard unavailable */ }
  }

  function download() {
    const body =
      `Wantok Telehealth — MFA recovery codes\n` +
      `Generated ${new Date().toISOString()}\n\n${text}\n\n` +
      `Keep these somewhere safe. Each code works once.\n`
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }))
    const a = document.createElement('a')
    a.href = url
    a.download = 'wantok-mfa-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
      <div className="text-sm font-semibold text-amber-900">Save your recovery codes</div>
      <p className="text-xs text-amber-800 mt-0.5">
        Shown <b>once</b>. Each lets you sign in a single time if you lose your code source.
      </p>
      <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-sm text-slate-800">
        {codes.map((c) => <span key={c}>{c}</span>)}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={copyAll} className="text-xs border border-amber-300 rounded px-2 py-1 hover:bg-amber-100">
          {copied ? 'Copied ✓' : 'Copy all'}
        </button>
        <button onClick={download} className="text-xs border border-amber-300 rounded px-2 py-1 hover:bg-amber-100">
          Download .txt
        </button>
        <button onClick={onDone} className="ml-auto text-xs text-amber-900 underline font-semibold">
          I've saved them
        </button>
      </div>
    </div>
  )
}

/**
 * Authenticator-app enrolment flow: fetch a secret + QR, let the user scan it,
 * then confirm one code. Calls onConfirmed(recoveryCodes) on success.
 */
function TotpEnrol({ onConfirmed, onCancel }) {
  const [data, setData] = useState(null) // { secret, otpauth_uri, qr_data_uri }
  const [code, setCode] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  // One-shot guard: under React.StrictMode this effect runs twice in dev, and
  // each totpSetup() mints a *fresh* server-side secret — racing the displayed
  // QR against what the server ultimately stores, so the scanned code never
  // matches. Fetch the secret exactly once per mount, and DON'T gate the result
  // on an `alive` flag (StrictMode's cleanup would otherwise discard the only
  // response and leave the panel stuck on "Preparing…"). A stray setState after
  // unmount is harmless in React 18.
  const startedRef = useRef(false)
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    totpSetup()
      .then((d) => setData(d))
      .catch((e) => setError(firstError(e, 'Could not start authenticator setup.')))
  }, [])

  async function confirm(e) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const res = await totpConfirm(code.trim())
      onConfirmed(res.recovery_codes || [])
    } catch (err) {
      setError(firstError(err, 'That code was not accepted — enter the current 6-digit code.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={confirm} className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
      <div className="text-sm font-semibold text-slate-800">Set up your authenticator app</div>
      <ol className="text-xs text-slate-600 list-decimal ml-4 space-y-1">
        <li>Open your authenticator app (Google Authenticator, Microsoft Authenticator, Authy…).</li>
        <li>Scan the QR code below, or enter the key manually.</li>
        <li>Enter the 6-digit code the app shows to confirm.</li>
      </ol>

      {!data && !error && <div className="text-sm text-slate-400">Preparing…</div>}
      {error && <ErrorBanner error={error} />}

      {data && (
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
          <img
            src={data.qr_data_uri}
            alt="Authenticator QR code"
            className="w-40 h-40 border border-slate-200 rounded bg-white p-1 shrink-0"
          />
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Manual key</div>
            <code className="block text-sm font-mono break-all bg-white border border-slate-200 rounded px-2 py-1 mt-1">
              {data.secret}
            </code>
          </div>
        </div>
      )}

      <label className="block">
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Code from your app</span>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          className="mt-1 w-40 border border-slate-300 rounded px-2 py-1.5 text-sm tracking-[0.3em] text-center font-mono"
        />
      </label>

      <div className="flex items-center gap-2 justify-end">
        <button type="button" onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-800">Cancel</button>
        <button
          type="submit"
          disabled={busy || !data || code.trim().length < 6}
          className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
        >
          {busy ? 'Confirming…' : 'Confirm & turn on'}
        </button>
      </div>
    </form>
  )
}

function StatusPill({ on, label }) {
  return (
    <span
      className={
        'inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border ' +
        (on ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-slate-100 text-slate-600 border-slate-200')
      }
    >
      {on ? `On · ${label}` : 'Off'}
    </span>
  )
}

export default function MfaSettingsCard() {
  const qc = useQueryClient()
  const [codes, setCodes] = useState(null)   // revealed-once recovery codes
  const [enrolling, setEnrolling] = useState(false) // TOTP enrol panel open

  const statusQ = useQuery({ queryKey: ['mfa', 'status'], queryFn: getMfaStatus })
  const s = statusQ.data
  const invalidate = () => qc.invalidateQueries({ queryKey: ['mfa', 'status'] })

  const enableEmailMut = useMutation({
    mutationFn: enableMfa,
    onSuccess: (data) => { setCodes(data.recovery_codes || []); invalidate() },
  })
  const disableAllMut = useMutation({
    mutationFn: disableMfa,
    onSuccess: () => { setCodes(null); invalidate() },
  })
  const regenMut = useMutation({
    mutationFn: regenerateRecoveryCodes,
    onSuccess: (data) => { setCodes(data.recovery_codes || []); invalidate() },
  })
  const totpDisableMut = useMutation({
    mutationFn: totpDisable,
    onSuccess: () => { setCodes(null); invalidate() },
  })

  const enabled = !!s?.enabled
  const totpOn = !!s?.totp_enabled
  const emailOn = !!s?.email_enabled
  const mandatory = !!s?.enforced_for_role && s?.enforcement === 'required'
  const anyError = enableEmailMut.error || disableAllMut.error || regenMut.error || totpDisableMut.error

  return (
    <Card>
      <CardHeader
        title="Two-factor authentication"
        subtitle="Require a second factor at sign-in. An authenticator app is the strongest option; email codes work without one and stay as a fallback."
      />

      {statusQ.isLoading && <div className="text-sm text-slate-400">Loading…</div>}
      {statusQ.error && <ErrorBanner error={statusQ.error} onRetry={() => statusQ.refetch()} />}

      {s && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill on={enabled} label="protected" />
            {mandatory && <span className="text-[11px] text-amber-700">Required for your role</span>}
            {enabled && (
              <span className="ml-auto text-[11px] text-slate-500">
                {s.unused_recovery_codes} recovery code{s.unused_recovery_codes === 1 ? '' : 's'} left
              </span>
            )}
          </div>

          {anyError && <div className="mt-3"><ErrorBanner error={anyError} /></div>}
          {codes && <RecoveryCodesPanel codes={codes} onDone={() => setCodes(null)} />}

          {/* ── Authenticator app ───────────────────────────── */}
          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-800">Authenticator app</span>
              <StatusPill on={totpOn} label="enabled" />
              <div className="ml-auto">
                {totpOn ? (
                  <button
                    onClick={() => { if (window.confirm('Remove your authenticator app? Email codes (if on) and recovery codes still work.')) totpDisableMut.mutate() }}
                    disabled={totpDisableMut.isPending}
                    className="text-sm border border-rose-200 text-rose-700 hover:bg-rose-50 rounded px-3 py-1.5 disabled:opacity-50"
                  >
                    Remove
                  </button>
                ) : (
                  !enrolling && (
                    <button
                      onClick={() => setEnrolling(true)}
                      className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-1.5"
                    >
                      Set up
                    </button>
                  )
                )}
              </div>
            </div>
            {enrolling && !totpOn && (
              <TotpEnrol
                onCancel={() => setEnrolling(false)}
                onConfirmed={(rc) => {
                  setEnrolling(false)
                  if (rc && rc.length) setCodes(rc)
                  invalidate()
                }}
              />
            )}
          </div>

          {/* ── Email codes ──────────────────────────────────── */}
          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-800">Email codes</span>
              <StatusPill on={emailOn} label="enabled" />
              <div className="ml-auto">
                {!emailOn && (
                  <button
                    onClick={() => enableEmailMut.mutate()}
                    disabled={enableEmailMut.isPending}
                    className="text-sm border border-slate-300 hover:border-wantok-400 rounded px-3 py-1.5 disabled:opacity-60"
                  >
                    {enableEmailMut.isPending ? 'Enabling…' : 'Enable'}
                  </button>
                )}
              </div>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              A 6-digit code is emailed at sign-in. Always available as a fallback once any factor is on.
            </p>
          </div>

          {/* ── Recovery codes + turn-off ────────────────────── */}
          {enabled && (
            <div className="mt-4 border-t border-slate-100 pt-4 flex flex-wrap items-center gap-2">
              <button
                onClick={() => regenMut.mutate()}
                disabled={regenMut.isPending}
                className="text-sm border border-slate-300 hover:border-wantok-400 rounded px-3 py-2 disabled:opacity-60"
              >
                {regenMut.isPending ? 'Generating…' : 'Regenerate recovery codes'}
              </button>
              <button
                onClick={() => { if (window.confirm('Turn off two-factor authentication entirely? Your account will be protected by password only.')) disableAllMut.mutate() }}
                disabled={disableAllMut.isPending || mandatory}
                title={mandatory ? 'MFA is mandatory for your role and cannot be turned off.' : undefined}
                className="ml-auto text-sm border border-rose-200 text-rose-700 hover:bg-rose-50 rounded px-3 py-2 disabled:opacity-50"
              >
                Turn off all
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  )
}
